import type { CoasterProject } from '../schema/index.ts';
import type { RideSummary } from '../ride/runner.ts';
import { autoBankNodes } from './operations.ts';

export const AUTO_BANK_SCALES = [-2, -1, -0.5, 0.25, 0.5, 0.75, 1, 1.5, 2] as const;
export const AUTO_BANK_MINIMUM_IMPROVEMENT_G = 0.05;
export const AUTO_BANK_GLOBAL_TOLERANCE_G = 0.01;

export interface AutoBankCandidate {
  readonly scale: (typeof AUTO_BANK_SCALES)[number];
  readonly project: CoasterProject;
  readonly totalBankChangeDegrees: number;
}

export interface DistanceInterval {
  readonly startDistance: number;
  readonly endDistance: number;
}

export function generateAutoBankCandidates(
  project: CoasterProject,
  nodeIds: readonly string[],
): readonly AutoBankCandidate[] {
  const seeded = autoBankNodes(project, nodeIds);
  if (!seeded.ok) return [];
  const selected = new Set(nodeIds);
  const seedById = new Map(seeded.project.track.nodes.map((node) => [node.id, node.bankDegrees]));
  const seen = new Set<string>();
  const candidates: AutoBankCandidate[] = [];

  for (const scale of AUTO_BANK_SCALES) {
    const next = structuredClone(project);
    let totalBankChangeDegrees = 0;
    for (const node of next.track.nodes) {
      if (!selected.has(node.id)) continue;
      const seededBank = seedById.get(node.id) ?? node.bankDegrees;
      const delta = seededBank - node.bankDegrees;
      const adjusted = Math.max(-55, Math.min(55, node.bankDegrees + delta * scale));
      totalBankChangeDegrees += Math.abs(adjusted - node.bankDegrees);
      node.bankDegrees = adjusted;
    }
    if (totalBankChangeDegrees < 0.001) continue;
    const key = next.track.nodes
      .filter((node) => selected.has(node.id))
      .map((node) => `${node.id}:${node.bankDegrees.toFixed(6)}`)
      .join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    candidates.push({ scale, project: next, totalBankChangeDegrees });
  }
  return candidates;
}

export function peakLateralGInIntervals(
  summary: RideSummary,
  intervals: readonly DistanceInterval[],
): number {
  if (!intervals.length) return Math.abs(summary.maxAbsLateralG.value);
  let peak = 0;
  for (const sample of summary.trace) {
    if (
      intervals.some(
        (interval) =>
          sample.distance >= interval.startDistance && sample.distance <= interval.endDistance,
      )
    )
      peak = Math.max(peak, Math.abs(sample.lateralG));
  }
  return peak;
}
